import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IonPage, useIonViewDidEnter } from '@ionic/react';
import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Container_v2 as Container,
  ScreenInput,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { PublicRoutes } from '../../../routes/navigation';
import { authNameSelector, signUpEmailSelector } from '../../../redux/auth/selector';
import { setSignUpName } from '../../../redux/auth/reducer';
import { useIsKeyBoardShown, useAnalytics } from '../../../shared';
import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';

const LastNamePage: FC<RouteComponentProps> = ({ history }) => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const isKeyboardShown = useIsKeyBoardShown();
  const { trackPredefinedEvent } = useAnalytics();

  const nameRef = useRef<HTMLInputElement | null>(null);

  const signUpEmail = useSelector(signUpEmailSelector);
  const nameData = useSelector(authNameSelector);

  const [isDisabled, setIsDisabled] = useState(true);

  useIonViewDidEnter(() => {
    if (!signUpEmail) {
      const webToWebAuth = localStorage.getItem('webToWebAuth');

      if (webToWebAuth) {
        history.push(PublicRoutes.AUTH);
      } else {
        history.push(PublicRoutes.EMAIL_SIGN_IN);
      }
      return;
    }
  }, [signUpEmail, history]);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
      screen_type: 'last_name',
    });
  });

  useIonViewDidEnter(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, [nameRef]);

  useEffect(() => {
    setIsDisabled(!nameData?.lastName?.length);
  }, [nameData]);

  const handleLastNameSubmit = useCallback(() => {
    if (isDisabled) return;

    trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
      screen_type: 'last_name',
      value: nameData.lastName,
    });
    history.push(PublicRoutes.DOB_SIGN_UP);
  }, [history, isDisabled, nameData.lastName, trackPredefinedEvent]);

  return (
    <IonPage>
      <AuthPageHeaderFeature backButtonLink={PublicRoutes.NAME_SIGN_UP} />
      <Container isFlexContainer>
        <Typography variant={TypographyVariantsEnum.Title28Semi}>{t('auth.create.surname.title')}</Typography>
        <ScreenInput
          ref={nameRef}
          placeholder={t('auth.create.surname.title')}
          value={nameData.lastName}
          type={'text'}
          maxLength={50}
          onChange={(e) => dispatch(setSignUpName({ lastName: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && handleLastNameSubmit()}
        />
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        <Button disabled={isDisabled} fullWidth size={'large'} onClick={handleLastNameSubmit}>
          {t('auth.button.next')}
        </Button>
      </AppFooter>
    </IonPage>
  );
};

export default LastNamePage;
